import { defineComponent } from 'vue';
import PowerVisBase from '@/components/modules/vis/PowerVisBase.vue';
import Tooltip from '@/components/modules/tooltips/Tooltip.vue';
import { serializeFilters, SupportedFiltersByIndex } from '@/logic/filters';
import { DefaultFacetTypesForIndex } from '@/logic/facets';
import { stats } from '@/services';
import { CommonQueryParameters } from '@/router/util';
const newspaperTermSorter = (data) => {
    const { items, meta, itemsDictionary } = data;
    // order of the items in the domain
    const domainOrder = Object.fromEntries(items?.map((item, index) => {
        return [item.domain.value, index];
    }) ?? []);
    // iterate over the items and sort the value items to match the domain order
    const fullySortedItems = items?.map(item => {
        const { domain, value } = item;
        const valueItems = value.items;
        const sortedValueItems = valueItems?.sort((a, b) => {
            const aDomainOrder = domainOrder[a.term] ?? 0;
            const bDomainOrder = domainOrder[b.term] ?? 0;
            return aDomainOrder - bDomainOrder;
        });
        return { domain, value: { ...value, items: sortedValueItems } };
    });
    return { meta, items: fullySortedItems, itemsDictionary };
};
const getSorter = (domain, facetType) => {
    if (domain === 'newspaper' && facetType == 'term')
        return newspaperTermSorter;
    return data => data;
};
const NoFacetFilters = {
    search: ['string'],
    tr_clusters: [],
    tr_passages: [],
};
const StatsQueryParams = {
    trc_vs_newspapers: {
        facet: 'newspaper',
        index: 'tr_passages',
        domain: 'time',
        groupby: 'textReuseCluster',
        unit: 'numbers.clusters',
    },
    tr_vs_newspapers: {
        facet: 'newspaper',
        index: 'tr_passages',
        domain: 'time',
        unit: 'numbers.passages',
    },
    // tr_vs_collections: {
    //   facet: 'collection',
    //   index: 'tr_passages',
    //   domain: 'time',
    // },
    trcsize_vs_time: {
        facet: 'textReuseClusterSize',
        index: 'tr_passages',
        domain: 'time',
    },
    trcsize_vs_newspaper: {
        facet: 'textReuseClusterSize',
        index: 'tr_passages',
        domain: 'newspaper',
        orderByOptions: ['count asc', 'count desc', 'mean asc', 'mean desc', 'max asc', 'max desc'],
        options: {
            bandwidth: 40,
            transformLabels: '',
            margin: { left: 150 },
            truncateLabelsAtLength: 20,
            floatingPointPrecision: 0,
        },
    },
    troverlap_vs_newspapers: {
        facet: 'textReuseClusterLexicalOverlap',
        index: 'tr_passages',
        domain: 'newspaper',
        orderByOptions: ['count asc', 'count desc', 'mean asc', 'mean desc', 'max asc', 'max desc'],
        options: {
            bandwidth: 40,
            transformLabels: '',
            margin: { left: 150 },
            truncateLabelsAtLength: 20,
            floatingPointPrecision: 2,
        },
    },
    troverlap_vs_time: {
        facet: 'textReuseClusterLexicalOverlap',
        index: 'tr_passages',
        domain: 'time',
    },
    trnewspapers_vs_newspapers: {
        facet: 'textReuseClusterNewspapers',
        index: 'tr_passages',
        domain: 'newspaper',
        groupby: 'textReuseCluster',
    },
    // troverlap_vs_newspapers: {},
    // trdelta_vs_newspapers: {},
    // trcount_vs_newspapers: {},
    // trcount_vs_time: {},
};
const VisualisationTypes = Object.keys(StatsQueryParams);
export default defineComponent({
    name: 'TextReuseStatistics',
    components: {
        PowerVisBase,
        Tooltip,
    },
    props: {
        filters: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        items: [],
        stats: {},
        statsLoading: false,
        visualisationTypes: VisualisationTypes,
        tooltip: {
            x: 0,
            y: 0,
            isActive: false,
            item: {
                term: null,
                count: 0,
            },
        },
    }),
    setup() {
        const itemClicked = () => { };
        return { itemClicked };
    },
    methods: {
        /**
         * @param {string} index
         * @param {string} type
         * @returns {boolean}
         */
        isFilterTypeSupporedInIndex(index, type) {
            // NOTE: daterange is the only filter type that does not have corresponding facet at the moment
            const filterTypes = DefaultFacetTypesForIndex[index].concat(['daterange']);
            return filterTypes.includes(type) && !NoFacetFilters[index].includes(type);
        },
        handleMousemove(event) {
            // if it is term, get the right label.
            const term = this.stats.itemsDictionary && event.point.closestItem?.term
                ? this.stats.itemsDictionary[event.point.closestItem.term]
                : event.point.closestItem?.term;
            this.tooltip = {
                x: event.point.x,
                y: event.point.y + 10,
                isActive: event.point.closestValueKey !== null,
                item: {
                    ...event.point.closestItem,
                    term,
                    valueKey: event.point.closestValueKey,
                    point: event.point.closestPoint,
                },
            };
        },
        handleMouseleave() {
            console.debug('handleMouseleave');
            this.tooltip = {
                x: 0,
                y: 0,
                isActive: false,
                item: {
                    term: null,
                    count: 0,
                },
            };
        },
        handleItemClicked({ item }) {
            if (!item)
                return;
            console.debug('handleItemClicked', item.term);
            this.$store.dispatch('selectionMonitor/show', {
                item: {
                    name: item.term,
                    uid: item.term,
                },
                searchIndex: 'tr_passages',
                type: 'newspaper',
            });
        },
    },
    computed: {
        // get visualisation type from URL parameters
        visualisation: {
            get() {
                const { [CommonQueryParameters.VisualisationType]: visualisation } = this.$route?.query;
                // if visualisation type is undefined or not in the list of options, return the first option
                if (typeof visualisation !== 'string' || !VisualisationTypes.includes(visualisation)) {
                    return VisualisationTypes[0];
                }
                return visualisation;
            },
            set(visualisation) {
                this.$navigation.updateQueryParameters({
                    [CommonQueryParameters.VisualisationType]: visualisation,
                });
            },
        },
        visualisationOrderByOptions() {
            return StatsQueryParams[this.visualisation].orderByOptions || [];
        },
        /** return a translation label string */
        visualisationLabel() {
            const unit = StatsQueryParams[this.visualisation].unit;
            if (!unit)
                return 'results';
            return unit;
        },
        /** get visualisation domain */
        visualisationDomain() {
            return StatsQueryParams[this.visualisation].domain;
        },
        /** options for current visualisation */
        visualisationOptions() {
            return StatsQueryParams[this.visualisation].options || {};
        },
        // get visualisation order by from URL parameters
        visualisationOrderBy: {
            get() {
                if (!this.visualisationOrderByOptions.length)
                    return null;
                const { [CommonQueryParameters.VisualisationOrderBy]: visualisationOrderBy, } = this.$route?.query;
                // if visualisation order by is undefined or not in the list of options, return the first option
                if (typeof visualisationOrderBy !== 'string' ||
                    !this.visualisationOrderByOptions.includes(visualisationOrderBy)) {
                    return this.visualisationOrderByOptions[0];
                }
                return visualisationOrderBy;
            },
            set(visualisationOrderBy) {
                this.$navigation.updateQueryParameters({
                    [CommonQueryParameters.VisualisationOrderBy]: visualisationOrderBy,
                });
            },
        },
        statsApiQueryParameters() {
            const { index, facet, domain, groupby } = StatsQueryParams[this.visualisation];
            const supportedFilterTypes = SupportedFiltersByIndex[index];
            const filters = this.filters;
            const supportedFilters = filters.filter(({ type }) => supportedFilterTypes.includes(type));
            // eslint-disable-next-line
            console.info('@statsApiQueryParameters check filters:', filters, '\n - using index:', index, '\n - supportedFilterTypes:', supportedFilterTypes, '\n - supportedFilters:', supportedFilters);
            const query = {
                index,
                facet,
                domain,
                stats: 'min,max,mean',
                sort: this.visualisationOrderBy,
                filters: serializeFilters(supportedFilters),
            };
            if (groupby) {
                query['groupby'] = groupby;
            }
            return {
                query,
                hash: JSON.stringify(query)
                    .split('')
                    .sort()
                    .join(''),
            };
        },
    },
    watch: {
        statsApiQueryParameters: {
            async handler({ query, hash }, previousValue) {
                if (previousValue && previousValue.hash === hash) {
                    return false;
                }
                // eslint-disable-next-line
                console.debug('[TextReuseOverview] @statsApiQueryParameters \n query:', query);
                try {
                    this.statsLoading = true;
                    const s = await stats.find({ query });
                    s.meta = s.meta || {};
                    s.meta.horizontal = true;
                    this.stats = s;
                    // const statsResult = await stats.find({ query })
                    // statsResult.meta = statsResult.meta || {}
                    // statsResult.meta.horizontal = true
                    // const toSortedItems = getSorter(statsResult.meta.domain, statsResult.meta.facetType)
                    // this.stats = this.visualisation === 'trc' ? toSortedItems(statsResult):statsResult
                    // // eslint-disable-next-line
                    // console.debug('[TextReuseOverview] @statsApiQueryParameters \n result:', this.stats)
                }
                finally {
                    this.statsLoading = false;
                }
            },
            immediate: true,
        },
    },
});
